import { locationAPI } from '@/lib/api';
import { resolveKnownAreaCoords } from '@/lib/assistantGeoSearch';
import { getSearchCountryScope } from '@/lib/countryScope';
import { getStoredUserLocation, saveUserLocation, type UserLocation } from '@/lib/location';

export type GeoSearchScope = {
    countryCode: string;
    cityId: number;
    cityName: string;
    cityLat?: number;
    cityLng?: number;
    /** Neighborhood / landmark label when catalog city is a metro sub-area (e.g. Banjara Hills). */
    areaName?: string;
};

const HYDERABAD_METRO_CITY = 'Hyderabad';

export interface CityConfig {
    name: string;
    center: { lat: number; lng: number };
    photonBbox?: string;
    defaultState: string;
}

export const CITIES_CONFIG: Record<string, CityConfig> = {
    hyderabad: {
        name: 'Hyderabad',
        center: { lat: 17.385, lng: 78.4867 },
        photonBbox: '78.10,17.20,78.75,17.65',
        defaultState: 'Telangana',
    },
    kolkata: {
        name: 'Kolkata',
        center: { lat: 22.5726, lng: 88.3639 },
        photonBbox: '88.15,22.35,88.60,22.80',
        defaultState: 'West Bengal',
    },
    bengaluru: {
        name: 'Bengaluru',
        center: { lat: 12.9716, lng: 77.5946 },
        photonBbox: '77.45,12.80,77.80,13.15',
        defaultState: 'Karnataka',
    },
    mumbai: {
        name: 'Mumbai',
        center: { lat: 19.0760, lng: 72.8777 },
        photonBbox: '72.75,18.85,73.05,19.35',
        defaultState: 'Maharashtra',
    },
    delhi: {
        name: 'Delhi',
        center: { lat: 28.6139, lng: 77.2090 },
        photonBbox: '76.85,28.40,77.40,28.90',
        defaultState: 'Delhi',
    },
    pune: {
        name: 'Pune',
        center: { lat: 18.5204, lng: 73.8567 },
        photonBbox: '73.65,18.35,74.05,18.70',
        defaultState: 'Maharashtra',
    },
    chennai: {
        name: 'Chennai',
        center: { lat: 13.0827, lng: 80.2707 },
        photonBbox: '80.10,12.90,80.35,13.25',
        defaultState: 'Tamil Nadu',
    },
};

export interface KnownCityInfo {
    center: { lat: number; lng: number };
    state: string;
    country?: string;
    photonBbox?: string;
}

export const KNOWN_CITIES_MAP: Record<string, KnownCityInfo> = {
    hyderabad: { center: { lat: 17.385, lng: 78.4867 }, state: 'Telangana', photonBbox: '78.10,17.20,78.75,17.65' },
    secunderabad: { center: { lat: 17.4399, lng: 78.4983 }, state: 'Telangana', photonBbox: '78.10,17.20,78.75,17.65' },
    cyberabad: { center: { lat: 17.4435, lng: 78.3772 }, state: 'Telangana', photonBbox: '78.10,17.20,78.75,17.65' },
    bengaluru: { center: { lat: 12.9716, lng: 77.5946 }, state: 'Karnataka', photonBbox: '77.45,12.80,77.80,13.15' },
    bangalore: { center: { lat: 12.9716, lng: 77.5946 }, state: 'Karnataka', photonBbox: '77.45,12.80,77.80,13.15' },
    mysore: { center: { lat: 12.2958, lng: 76.6394 }, state: 'Karnataka' },
    mysuru: { center: { lat: 12.2958, lng: 76.6394 }, state: 'Karnataka' },
    mangalore: { center: { lat: 12.9141, lng: 74.8560 }, state: 'Karnataka' },
    mumbai: { center: { lat: 19.0760, lng: 72.8777 }, state: 'Maharashtra', photonBbox: '72.75,18.85,73.05,19.35' },
    bombay: { center: { lat: 19.0760, lng: 72.8777 }, state: 'Maharashtra', photonBbox: '72.75,18.85,73.05,19.35' },
    'navi mumbai': { center: { lat: 19.0330, lng: 73.0297 }, state: 'Maharashtra' },
    thane: { center: { lat: 19.2183, lng: 72.9781 }, state: 'Maharashtra' },
    pune: { center: { lat: 18.5204, lng: 73.8567 }, state: 'Maharashtra', photonBbox: '73.65,18.35,74.05,18.70' },
    pcmc: { center: { lat: 18.6298, lng: 73.7997 }, state: 'Maharashtra' },
    nagpur: { center: { lat: 21.1458, lng: 79.0882 }, state: 'Maharashtra' },
    nashik: { center: { lat: 19.9975, lng: 73.7898 }, state: 'Maharashtra' },
    delhi: { center: { lat: 28.6139, lng: 77.2090 }, state: 'Delhi', photonBbox: '76.85,28.40,77.40,28.90' },
    'new delhi': { center: { lat: 28.6139, lng: 77.2090 }, state: 'Delhi', photonBbox: '76.85,28.40,77.40,28.90' },
    gurgaon: { center: { lat: 28.4595, lng: 77.0266 }, state: 'Haryana' },
    gurugram: { center: { lat: 28.4595, lng: 77.0266 }, state: 'Haryana' },
    noida: { center: { lat: 28.5355, lng: 77.3910 }, state: 'Uttar Pradesh' },
    'greater noida': { center: { lat: 28.4744, lng: 77.5040 }, state: 'Uttar Pradesh' },
    ghaziabad: { center: { lat: 28.6692, lng: 77.4538 }, state: 'Uttar Pradesh' },
    faridabad: { center: { lat: 28.4089, lng: 77.3178 }, state: 'Haryana' },
    chennai: { center: { lat: 13.0827, lng: 80.2707 }, state: 'Tamil Nadu', photonBbox: '80.10,12.90,80.35,13.25' },
    madras: { center: { lat: 13.0827, lng: 80.2707 }, state: 'Tamil Nadu', photonBbox: '80.10,12.90,80.35,13.25' },
    coimbatore: { center: { lat: 11.0168, lng: 76.9558 }, state: 'Tamil Nadu' },
    madurai: { center: { lat: 9.9252, lng: 78.1198 }, state: 'Tamil Nadu' },
    kolkata: { center: { lat: 22.5726, lng: 88.3639 }, state: 'West Bengal', photonBbox: '88.15,22.35,88.60,22.80' },
    calcutta: { center: { lat: 22.5726, lng: 88.3639 }, state: 'West Bengal', photonBbox: '88.15,22.35,88.60,22.80' },
    howrah: { center: { lat: 22.5958, lng: 88.2636 }, state: 'West Bengal' },
    siliguri: { center: { lat: 26.7271, lng: 88.3953 }, state: 'West Bengal' },
    durgapur: { center: { lat: 23.5204, lng: 87.3119 }, state: 'West Bengal' },
    ahmedabad: { center: { lat: 23.0225, lng: 72.5714 }, state: 'Gujarat' },
    surat: { center: { lat: 21.1702, lng: 72.8311 }, state: 'Gujarat' },
    jaipur: { center: { lat: 26.9124, lng: 75.7873 }, state: 'Rajasthan' },
    lucknow: { center: { lat: 26.8467, lng: 80.9462 }, state: 'Uttar Pradesh' },
    kanpur: { center: { lat: 26.4499, lng: 80.3319 }, state: 'Uttar Pradesh' },
    chandigarh: { center: { lat: 30.7333, lng: 76.7794 }, state: 'Chandigarh' },
    kochi: { center: { lat: 9.9312, lng: 76.2673 }, state: 'Kerala' },
    thiruvananthapuram: { center: { lat: 8.5241, lng: 76.9366 }, state: 'Kerala' },
    visakhapatnam: { center: { lat: 17.6868, lng: 83.2185 }, state: 'Andhra Pradesh' },
    vijayawada: { center: { lat: 16.5062, lng: 80.6480 }, state: 'Andhra Pradesh' },
    austin: { center: { lat: 30.2672, lng: -97.7431 }, state: 'Texas', country: 'USA' },
};

export const KNOWN_CITY_COORDS: Record<string, { lat: number; lng: number }> = Object.fromEntries(
    Object.entries(KNOWN_CITIES_MAP).map(([k, v]) => [k, v.center])
);

export function resolveCityConfig(cityName?: string | null): CityConfig {
    const raw = (cityName || '').trim();
    const key = raw.toLowerCase();
    if (!key) return CITIES_CONFIG.hyderabad;

    // Canonical metro aliases → shared city config (centers, bbox, default state)
    if (key === 'calcutta' || key === 'kolkata') return CITIES_CONFIG.kolkata;
    // Twin-city / GHMC zones → Hyderabad (same as single-property listing)
    if (key === 'hyderabad' || key === 'secunderabad' || key === 'cyberabad') return CITIES_CONFIG.hyderabad;
    if (key === 'bengaluru' || key === 'bangalore') return CITIES_CONFIG.bengaluru;
    if (key === 'mumbai' || key === 'bombay' || key === 'navi mumbai' || key === 'thane') return CITIES_CONFIG.mumbai;
    if (key === 'delhi' || key === 'new delhi') return CITIES_CONFIG.delhi;
    if (key === 'pune' || key === 'pcmc') return CITIES_CONFIG.pune;
    if (key === 'chennai' || key === 'madras') return CITIES_CONFIG.chennai;

    if (CITIES_CONFIG[key]) return CITIES_CONFIG[key];

    const known = KNOWN_CITIES_MAP[key];
    if (known) {
        const displayName = raw
            .split(/\s+/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
        return {
            name: displayName,
            center: known.center,
            defaultState: known.state,
            photonBbox: known.photonBbox,
        };
    }

    return CITIES_CONFIG.hyderabad;
}

function resolveCityCenterCoords(
    cityName: string,
    fallbackLat?: number,
    fallbackLng?: number,
): { lat?: number; lng?: number } {
    const key = cityName.trim().toLowerCase();
    if (KNOWN_CITY_COORDS[key]) {
        return KNOWN_CITY_COORDS[key];
    }
    const areaCoords = resolveKnownAreaCoords(cityName);
    if (areaCoords) {
        return areaCoords;
    }
    return { lat: fallbackLat, lng: fallbackLng };
}

function isHyderabadMetroArea(name: string): boolean {
    const normalized = name.trim().toLowerCase();
    if (!normalized || normalized === HYDERABAD_METRO_CITY.toLowerCase()) {
        return false;
    }
    return Boolean(resolveKnownAreaCoords(name));
}

/** Catalog suburbs (e.g. Banjara Hills id 126151) map to Hyderabad for geo search + landmarks. */
export function applyMetroAreaRemap(scope: GeoSearchScope, areaHint?: string): GeoSearchScope {
    const areaCandidate = (areaHint || scope.areaName || scope.cityName).trim();
    if (scope.countryCode !== 'IN' || !isHyderabadMetroArea(areaCandidate)) {
        return scope;
    }
    const coords = resolveKnownAreaCoords(areaCandidate);
    return {
        countryCode: scope.countryCode,
        cityId: scope.cityName.toLowerCase() === HYDERABAD_METRO_CITY.toLowerCase()
            ? scope.cityId
            : 1,
        cityName: HYDERABAD_METRO_CITY,
        cityLat: coords?.lat ?? scope.cityLat,
        cityLng: coords?.lng ?? scope.cityLng,
        areaName: areaCandidate,
    };
}

function scopeFromStoredLocation(): GeoSearchScope | null {
    const loc = getStoredUserLocation();
    if (!loc?.city?.trim() || loc.catalog_enabled === false) {
        return null;
    }
    if (!loc.city_id || loc.city_id <= 0) {
        return null;
    }
    const countryScope = getSearchCountryScope();
    const countryCode = (loc.countryCode || countryScope?.code || 'IN').trim().toUpperCase();
    const cityName = loc.city.trim();
    const cityCoords = resolveCityCenterCoords(cityName, loc.lat, loc.lon);
    const raw: GeoSearchScope = {
        countryCode,
        cityId: loc.city_id,
        cityName,
        cityLat: cityCoords.lat,
        cityLng: cityCoords.lng,
        ...(loc.area?.trim() ? { areaName: loc.area.trim() } : {}),
    };
    return applyMetroAreaRemap(raw, loc.area || loc.city);
}

function persistRemappedLocation(loc: Partial<UserLocation>, scope: GeoSearchScope): void {
    saveUserLocation({
        city: scope.cityName,
        state: loc.state || '',
        country: loc.country || '',
        countryCode: scope.countryCode,
        catalog_enabled: true,
        city_id: scope.cityId,
        state_id: loc.state_id,
        country_id: loc.country_id,
        lat: scope.cityLat ?? loc.lat,
        lon: scope.cityLng ?? loc.lon,
        area: scope.areaName || loc.area,
        pincode: loc.pincode,
    });
}

/** Read geo search scope from persisted catalog location (sync). */
export function getGeoSearchScope(): GeoSearchScope | null {
    return scopeFromStoredLocation();
}

async function resolveHyderabadCityId(countryCode: string): Promise<number | null> {
    if (countryCode !== 'IN') return null;
    try {
        const data = await locationAPI.getSupportedCities(countryCode, HYDERABAD_METRO_CITY, 0, 5);
        const match = (data.cities || []).find(
            (c) => c.name.trim().toLowerCase() === HYDERABAD_METRO_CITY.toLowerCase(),
        );
        return match?.id ?? null;
    } catch {
        return null;
    }
}

async function finalizeMetroScope(
    scope: GeoSearchScope,
    loc: Partial<UserLocation> & { city: string },
    options: { persistOnLookup?: boolean } = {},
): Promise<GeoSearchScope> {
    let remapped = applyMetroAreaRemap(scope, loc.area || loc.city);
    if (remapped.areaName && remapped.countryCode === 'IN') {
        const hyderabadId = await resolveHyderabadCityId(remapped.countryCode);
        if (hyderabadId) {
            remapped = { ...remapped, cityId: hyderabadId };
        }
    }
    const shouldPersist =
        options.persistOnLookup
        || (remapped.areaName != null
            && (remapped.cityId !== loc.city_id || loc.city !== remapped.cityName));
    if (shouldPersist) {
        persistRemappedLocation(loc, remapped);
    }
    return remapped;
}

/** Resolve geo scope from storage or catalog API lookup by city name. */
export async function resolveGeoSearchScope(): Promise<GeoSearchScope | null> {
    const loc = getStoredUserLocation();
    if (!loc?.city?.trim() || loc.catalog_enabled === false) {
        return null;
    }

    const existing = getGeoSearchScope();
    if (existing) {
        return finalizeMetroScope(existing, { ...loc, city: loc.city.trim() });
    }

    const countryScope = getSearchCountryScope();
    const countryCode = (loc.countryCode || countryScope?.code || 'IN').trim().toUpperCase();
    const cityName = loc.city.trim();

    try {
        const data = await locationAPI.getSupportedCities(countryCode, cityName, 0, 20);
        const match = (data.cities || []).find(
            (c) => c.name.trim().toLowerCase() === cityName.toLowerCase(),
        );
        if (!match?.id) {
            return null;
        }

        const cityCoords = resolveCityCenterCoords(match.name, match.latitude ?? loc.lat, match.longitude ?? loc.lon);
        const raw: GeoSearchScope = {
            countryCode,
            cityId: match.id,
            cityName: match.name,
            cityLat: cityCoords.lat,
            cityLng: cityCoords.lng,
            ...(loc.area?.trim() ? { areaName: loc.area.trim() } : {}),
        };

        return finalizeMetroScope(raw, { ...loc, city: cityName }, { persistOnLookup: true });
    } catch {
        return null;
    }
}

/** Default map viewport for a geo scope (matches GeoSearchChatPage). */
export function defaultGeoMapViewport(
    countryCode: string,
    cityLat?: number,
    cityLng?: number,
): { lat: number; lng: number; zoom: number } {
    if (
        cityLat != null
        && cityLng != null
        && Number.isFinite(cityLat)
        && Number.isFinite(cityLng)
    ) {
        return { lat: cityLat, lng: cityLng, zoom: countryCode === 'US' ? 11 : 12 };
    }
    return countryCode === 'US'
        ? { lat: 30.2672, lng: -97.7431, zoom: 11 }
        : { lat: 17.385, lng: 78.4867, zoom: 12 };
}
